"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useBundleContext = exports.BundleProvider = void 0;
var _react = _interopRequireWildcard(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _functions = require("../utils/functions");
var _configContext = require("./config-context");
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function (e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && {}.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
const BundleContext = /*#__PURE__*/(0, _react.createContext)();

/**
 * BundleProvider
 *
 * Manages bundle-specific state and operations.
 * Separated from ConfigContext to prevent unnecessary re-renders.
 *
 * @param {Object} props The component props
 * @param {ReactNode} props.children The children components
 * @returns {ReactNode} BundleProvider
 */
const BundleProvider = ({
  children
}) => {
  const {
    configData,
    updateInstance
  } = (0, _configContext.useConfig)();
  const [previousState, setPreviousState] = (0, _react.useState)({});

  // Bundle state and functions
  const bundleState = (0, _react.useMemo)(() => {
    if (!configData?.instance) return {
      isActive: false
    };
    const activeBundleId = (0, _functions.getActiveBundleId)(configData.instance);
    if (!activeBundleId) return {
      isActive: false
    };
    const bundleData = configData.instance[activeBundleId];
    const bundleProperties = bundleData.properties;
    return {
      isActive: true,
      domain: bundleProperties.domain,
      includedProducts: bundleProperties.includedProducts ? [...bundleProperties.includedProducts, 'term'] : []
    };
  }, [configData?.instance]);
  const deactivateBundle = (0, _react.useCallback)(async bundleId => {
    const currentInstance = {
      ...configData?.instance
    };
    if (previousState && Object.keys(previousState).length > 0) {
      Object.keys(previousState).forEach(productKey => {
        if (currentInstance[productKey]) {
          currentInstance[productKey] = {
            ...currentInstance[productKey],
            ...previousState[productKey]
          };
        }
      });
    }

    // Deactivate bundle
    currentInstance[bundleId].properties.selection = 'none';

    // Clear the previous state
    setPreviousState({});
    await updateInstance(currentInstance);
  }, [configData?.instance, updateInstance, previousState]);
  const toggleBundle = (0, _react.useCallback)(async (bundleId, newState) => {
    if (!configData?.instance) return;
    const currentInstance = {
      ...configData.instance
    };
    const currentData = {
      ...configData.data
    };
    const bundleData = currentInstance[bundleId];
    if (!bundleData) return;

    // 1. Check if bundle is already active (toggle behavior)
    if (!newState) {
      await deactivateBundle(bundleId);
      return;
    }

    // 2. Capture current state of included products and target products
    const bundleProperties = bundleData.properties;
    const includedProducts = bundleProperties.includedProducts;
    const targetProducts = [];
    const capturedPreviousState = {};
    includedProducts.forEach(productKey => {
      if (currentInstance[productKey]) {
        targetProducts.push(productKey);
        capturedPreviousState[productKey] = {
          properties: structuredClone(currentInstance[productKey].properties),
          addons: structuredClone(currentInstance[productKey].addons)
        };
      }
    });

    // 3. Apply bundle defaults to target products
    targetProducts.forEach(targetProduct => {
      if (currentInstance[targetProduct] && currentData[targetProduct]) {
        includedProducts.forEach(productKey => {
          let defaultSelection;
          const bundleTerm = bundleProperties.term;

          // Check if this is an addon for the target product
          if (currentData[targetProduct].addons?.[productKey]) {
            const addonData = currentData[targetProduct].addons?.[productKey];
            defaultSelection = addonData?.attributes?.enum?.[0];
          }
          const newData = {
            ...(defaultSelection && {
              selection: defaultSelection
            }),
            ...(bundleTerm && {
              term: bundleTerm
            })
          };

          // Update data if it exists
          if (currentInstance[targetProduct].addons?.[productKey]) {
            currentInstance[targetProduct].addons[productKey] = {
              ...currentInstance[targetProduct].addons[productKey],
              ...newData
            };
          } else if (currentInstance[productKey]?.properties) {
            currentInstance[productKey].properties = {
              ...currentInstance[productKey].properties,
              ...newData
            };
          }
        });
      }
    });

    // 4. Activate bundle and store previous state
    currentInstance[bundleId].properties.selection = bundleId;
    setPreviousState(capturedPreviousState);
    await updateInstance(currentInstance);
  }, [configData?.instance, configData?.data, updateInstance, deactivateBundle]);
  const isComponentIncluded = (0, _react.useCallback)(instancePath => {
    if (!bundleState.isActive) return false;
    const [productKey,, itemName] = (0, _functions.parseInstancePath)(instancePath);
    const includedProducts = bundleState.includedProducts;
    if (productKey === bundleState.domain) {
      return includedProducts.includes(productKey) && includedProducts.includes(itemName);
    }
    return includedProducts.includes(productKey);
  }, [bundleState]);
  const contextValue = (0, _react.useMemo)(() => ({
    bundleState,
    toggleBundle,
    isComponentIncluded
  }), [bundleState, toggleBundle, isComponentIncluded]);
  return /*#__PURE__*/_react.default.createElement(BundleContext.Provider, {
    value: contextValue
  }, children);
};
exports.BundleProvider = BundleProvider;
BundleProvider.propTypes = {
  children: _propTypes.default.node.isRequired
};
const useBundleContext = () => {
  const context = (0, _react.useContext)(BundleContext);
  if (!context) {
    throw new Error('useBundleContext must be used within a BundleProvider');
  }
  return context;
};
exports.useBundleContext = useBundleContext;